// Volcano Peak — post-game high-level map.
// Shares the RouteMap interface defined in route1.ts.

import { TileType } from "@/engine/constants";
import {
  type RouteMap,
  type EncounterZone,
  type MapExit,
  type TrainerPlacement,
} from "./route1";

// ─── Layout constants ─────────────────────────────────────────────────────────

const WIDTH = 20;
const HEIGHT = 20;

// Center corridor runs x 8–12 (5 tiles wide).
const CORRIDOR_X_START = 8;
const CORRIDOR_X_END = 12; // inclusive

// Lava lake clusters (3×3 Ocean blocks).
// West lake: top-left at (2, 8); East lake: top-left at (15, 8).
const WEST_LAKE = { x: 2, y: 8 };
const EAST_LAKE = { x: 15, y: 8 };
const LAKE_SIZE = 3;

// ─── Tile builder ─────────────────────────────────────────────────────────────

function buildTileMaps(): { tileMap: number[][]; collisionMap: boolean[][] } {
  const tileMap: number[][] = [];
  const collisionMap: boolean[][] = [];

  // Pre-compute lava lake positions for O(1) lookup.
  const lavaSet = new Set<string>();
  for (let dy = 0; dy < LAKE_SIZE; dy++) {
    for (let dx = 0; dx < LAKE_SIZE; dx++) {
      lavaSet.add(`${WEST_LAKE.x + dx},${WEST_LAKE.y + dy}`);
      lavaSet.add(`${EAST_LAKE.x + dx},${EAST_LAKE.y + dy}`);
    }
  }

  for (let y = 0; y < HEIGHT; y++) {
    const tileRow: number[] = [];
    const collRow: boolean[] = [];

    for (let x = 0; x < WIDTH; x++) {
      const onBorder = x === 0 || x === WIDTH - 1 || y === 0 || y === HEIGHT - 1;
      const onCorridor = x >= CORRIDOR_X_START && x <= CORRIDOR_X_END;
      const onLava = lavaSet.has(`${x},${y}`);

      // Encounter zones flank the corridor away from the border strip.
      // West zone: x 1–6, y 4–15. East zone: x 14–18, y 4–15.
      const onWestZone = x >= 1 && x <= 6 && y >= 4 && y <= 15;
      const onEastZone = x >= 14 && x <= 18 && y >= 4 && y <= 15;

      let tile: TileType;
      let blocked: boolean;

      if (onBorder) {
        tile = TileType.Wall;
        blocked = true;
      } else if (onLava) {
        // Lava lake — impassable (Ocean tile represents lava visually).
        tile = TileType.Ocean;
        blocked = true;
      } else if (onCorridor) {
        tile = TileType.BuildingFloor;
        blocked = false;
      } else if (onWestZone || onEastZone) {
        // "Hot zones" — re-purposed Grass tiles trigger wild encounters.
        tile = TileType.Grass;
        blocked = false;
      } else {
        // Remaining interior rock — impassable walls.
        tile = TileType.Wall;
        blocked = true;
      }

      tileRow.push(tile);
      collRow.push(blocked);
    }

    tileMap.push(tileRow);
    collisionMap.push(collRow);
  }

  return { tileMap, collisionMap };
}

const { tileMap, collisionMap } = buildTileMaps();

// ─── Encounter zones ──────────────────────────────────────────────────────────

const encounterZones: EncounterZone[] = [
  {
    id: "volcano_west",
    bounds: { x: 1, y: 4, w: 5, h: 12 },
    speciesPool: [
      { speciesId: "22", weight: 30, levelMin: 30, levelMax: 40 }, // Algomancer
      { speciesId: "24", weight: 30, levelMin: 28, levelMax: 38 }, // Inframonk
      { speciesId: "20", weight: 25, levelMin: 32, levelMax: 42 }, // Auditlord
      { speciesId: "26", weight: 10, levelMin: 35, levelMax: 45 }, // Cryptoquant
      { speciesId: "29", weight: 5,  levelMin: 40, levelMax: 50 }, // Synthlord
    ],
  },
  {
    id: "volcano_east",
    bounds: { x: 14, y: 4, w: 5, h: 12 },
    speciesPool: [
      { speciesId: "18", weight: 30, levelMin: 30, levelMax: 40 }, // Trendlion
      { speciesId: "14", weight: 30, levelMin: 28, levelMax: 38 }, // Hiremaster
      { speciesId: "25", weight: 15, levelMin: 35, levelMax: 45 }, // Pitchmaster
      { speciesId: "27", weight: 15, levelMin: 32, levelMax: 42 }, // Growthhive
      { speciesId: "30", weight: 10, levelMin: 40, levelMax: 50 }, // Visionfox
    ],
  },
];

// ─── Exits ────────────────────────────────────────────────────────────────────

const exits: MapExit[] = [
  { x: 10, y: 19, targetMapId: "route_1",    targetX: 12, targetY: 0  }, // back to Route 1
  { x: 10, y: 0,  targetMapId: "corp_tower", targetX: 10, targetY: 19 }, // to Corp Tower
];

// ─── Trainer placements ───────────────────────────────────────────────────────

const trainers: TrainerPlacement[] = [
  { trainerId: "trainer_volcano_a", x: 7,  y: 8  },
  { trainerId: "trainer_volcano_b", x: 13, y: 12 },
];

// ─── Map export ───────────────────────────────────────────────────────────────

export const VOLCANO_PEAK_MAP: RouteMap = {
  id: "volcano_peak",
  name: "Volcano Peak",
  type: "overworld",
  width: WIDTH,
  height: HEIGHT,
  tileMap,
  collisionMap,
  interactables: [],
  encounterZones,
  exits,
  trainers,
};
